import React, { useState, useEffect } from 'react';
import { BarChart3, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../lib/auth';
import { getAccessibleClientIds } from '../lib/multiClientUtils';
import { useStatistics } from '../hooks/useStatistics';
import StatisticsPeriodSelector, { getPresetDates, PeriodPreset } from '../components/statistics/StatisticsPeriodSelector';
import StatisticsKpiCards from '../components/statistics/StatisticsKpiCards';
import StatisticsActivityCards from '../components/statistics/StatisticsActivityCards';
import StatisticsEvolutionChart from '../components/statistics/StatisticsEvolutionChart';
import StatisticsAppointmentFunnel from '../components/statistics/StatisticsAppointmentFunnel';

import StatisticsRecentAppointments from '../components/statistics/StatisticsRecentAppointments';

interface ClientOption {
  id: string;
  name: string;
}

const StatisticsPage: React.FC = () => {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);

  const initialDates = getPresetDates('last_month');
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);

  useEffect(() => {
    const fetchClients = async () => {
      if (!profile) return;

      try {
        if (isAdmin) {
          const { data, error } = await supabase
            .from('clients')
            .select('id, name')
            .eq('statut', 'actif')
            .order('name');

          if (!error && data && data.length > 0) {
            setClients(data);
            setSelectedClientId(data[0].id);
          }
        } else {
          const clientIds = await getAccessibleClientIds(profile.user_id, profile.role);
          if (clientIds.length > 0) {
            const { data, error } = await supabase
              .from('clients')
              .select('id, name')
              .in('id', clientIds);

            if (!error && data && data.length > 0) {
              setClients(data);
              setSelectedClientId(data[0].id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
  }, [profile, isAdmin]);

  const {
    loading,
    kpis,
    chartData,
    normalizedAppointments,
    activityMetrics,
    recentAppointments,
  } = useStatistics(selectedClientId, startDate, endDate);

  const handleDateChange = (start: string, end: string, _preset: PeriodPreset) => {
    setStartDate(start);
    setEndDate(end);
  };

  const selectedClientName = clients.find((c) => c.id === selectedClientId)?.name || '';

  if (clientsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-500 text-sm">Chargement...</span>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun client accessible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
        >
          <div>
            <div className="flex items-center space-x-3">
              <div className="bg-rose-50 p-2.5 rounded-lg">
                <BarChart3 className="h-6 w-6 text-rose-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Statistiques</h1>
                <p className="text-sm text-gray-500 mt-0.5">Suivi de vos resultats de prospection</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {isAdmin && clients.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
                  className="inline-flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm min-w-[180px]"
                >
                  <span className="truncate">{selectedClientName}</span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ${clientDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {clientDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setClientDropdownOpen(false)} />
                    <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 max-h-72 overflow-y-auto">
                      {clients.map((client) => (
                        <button
                          key={client.id}
                          onClick={() => {
                            setSelectedClientId(client.id);
                            setClientDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            selectedClientId === client.id
                              ? 'bg-rose-50 text-rose-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {client.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <StatisticsPeriodSelector
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateChange}
            />
          </div>
        </motion.div>

        <div className="space-y-8">
          <StatisticsKpiCards kpis={kpis} loading={loading} />

          <StatisticsActivityCards metrics={activityMetrics} loading={loading} />

          <StatisticsEvolutionChart
            data={chartData}
            loading={loading}
          />

          <StatisticsAppointmentFunnel
            appointments={normalizedAppointments}
            loading={loading}
          />

          <StatisticsRecentAppointments appointments={recentAppointments} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;
